// Going full screen on a hall screen, by hand or by address bar.
//
// WHY THE URL CANNOT SIMPLY DEMAND IT. Every browser refuses requestFullscreen
// outside a user gesture, and the refusal is deliberate: a page that could put
// itself full screen on load could put itself full screen over a bank. So
// `?fullscreen=1` cannot be honoured on arrival. What it can do is arm the
// request and spend the first tap on it, which for a stand tablet is the same
// thing — somebody touches the screen to start anyway — and for a laptop being
// set up with a mouse it is one click on the corner control instead.
//
// BOTH ROUTES EXIST BECAUSE NEITHER ALWAYS WORKS. iPhone Safari has no element
// full screen at all, so the control hides itself rather than offering a button
// that does nothing. The flag is what a crew member can put in a saved
// shortcut without knowing which tablet they will be handed.

import { useEffect, useState, type ReactNode } from "react";

/** Whether the address bar asked for full screen. `?fullscreen=1`, or `?fs=1`. */
export function fullscreenWanted(search: string): boolean {
  const asked = new URLSearchParams(search);
  const value = asked.get("fullscreen") ?? asked.get("fs");
  return value === "1" || value === "true" || value === "";
}

function available(): boolean {
  if (typeof document === "undefined") return false;
  // fullscreenEnabled is false inside an iframe that was not given the
  // permission, which is exactly when the button should not be there.
  return document.fullscreenEnabled === true;
}

function enter(): void {
  void document.documentElement.requestFullscreen?.().catch(() => {
    // Refused: no gesture, or the device does not do this. Nothing to say to
    // somebody standing at a stand, and the control stays where it was.
  });
}

export function FullscreenControl({ armed = false }: { armed?: boolean }): ReactNode {
  const [on, setOn] = useState(false);
  const [visible, setVisible] = useState(true);
  const [can] = useState(available);

  useEffect(() => {
    if (!can) return undefined;
    const sync = (): void => {
      const entered = document.fullscreenElement !== null;
      setOn(entered);
      if (entered) setVisible(false);
    };
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [can]);

  useEffect(() => {
    if (!can) return undefined;
    const timer = window.setTimeout(() => setVisible(false), 15_000);
    return () => window.clearTimeout(timer);
  }, [can]);

  useEffect(() => {
    if (!can || !armed) return undefined;
    // `once` so a person who leaves full screen on purpose is not dragged back
    // into it by their next tap.
    const spend = (): void => {
      if (document.fullscreenElement === null) enter();
    };
    document.addEventListener("pointerdown", spend, { once: true, capture: true });
    return () => document.removeEventListener("pointerdown", spend, { capture: true });
  }, [can, armed]);

  if (!can || !visible) return null;

  return (
    <button
      type="button"
      className="web-fullscreen"
      aria-label={on ? "Leave full screen" : "Full screen"}
      title={on ? "Leave full screen" : "Full screen"}
      onClick={() => {
        setVisible(false);
        if (document.fullscreenElement !== null) void document.exitFullscreen().catch(() => {});
        else enter();
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d={
            on
              ? "M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"
              : "M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"
          }
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
